import {
  chooseServingShelf,
  groupByUrgency,
  type ServingCandidate,
  type ServingMovesResponse,
  type ServingShelfOption,
} from "@corklog/shared";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { fetchServingMoves, moveBottles } from "../api.js";
import { useMayWrite } from "../cellar.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { OwnersOnly } from "../components/OwnersOnly.js";
import { DrinkingWindow } from "../components/DrinkingWindow.js";
import {
  actionButton,
  fieldLabel,
  narrowed,
  resting,
  Select,
} from "../components/controls.js";
import { useReload } from "../offline.js";

/**
 * What to carry from the ageing cabinet to the fridge.
 *
 * Task `8.12`, and the only screen in this application that asks somebody to
 * go and do something physical. Everything else writes down what has already
 * happened; this says *those three, now*, and then writes down that it was
 * done.
 *
 * **It is short on purpose.** The rule in `shared/src/serving.ts` takes what
 * is ready today — closed, closing, at its peak, just opened — and leaves the
 * next three years where they are. On this cellar that is one bottle against
 * thirty-one, and the thirty-one are counted at the foot rather than listed:
 * a prompt naming a third of the ageing cabinet is not a prompt, it is a
 * second cabinet. Decided 2026-09-15 in `project-scope.md`.
 *
 * **The shelf is proposed, never imposed.** A serving cabinet with two
 * temperature zones has two right answers and the cellar knows which is which,
 * so the champagne opens on the cold shelf and the Barolo on the warm one —
 * and where no shelf records a temperature nothing is proposed at all, because
 * a default chosen with no information is a guess wearing the clothes of a
 * suggestion.
 *
 * **Moving needs the network, and says so when it fails.** Unlike the walk at
 * `8.2` this is not held in the browser until it can be sent: the act is
 * performed at the cabinet door, where the phone is, and a move is one request
 * that either happened or did not.
 */
const CURRENT_YEAR = new Date().getFullYear();

type State =
  | { status: "loading" }
  | { status: "ready"; moves: ServingMovesResponse }
  | { status: "failed" };

/** How the carrying went, once somebody says they carried it. */
type MoveState =
  | { status: "idle" }
  | { status: "moving" }
  | { status: "moved"; bottles: number; overfull: readonly string[] }
  | { status: "failed" };

/** What the page has been told to move: how many of each wine, and where to. */
type Chosen = {
  readonly count: number;
  readonly shelfId: string;
};

export function ToServe() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const mayWrite = useMayWrite();
  const [state, setState] = useState<State>({ status: "loading" });
  const [chosen, setChosen] = useState<ReadonlyMap<string, Chosen>>(new Map());
  const [move, setMove] = useState<MoveState>({ status: "idle" });

  const load = useCallback(() => {
    void fetchServingMoves(cellarId)
      .then((moves) => setState({ status: "ready", moves }))
      .catch(() => setState({ status: "failed" }));
  }, [cellarId]);

  useReload(load);

  const moves = state.status === "ready" ? state.moves : null;
  const shelves = moves?.shelves ?? [];
  const candidates = useMemo(() => moves?.candidates ?? [], [moves]);

  // The same grouping, in the same order, under the same headings as *Att
  // öppna* — it is the same question, asked of the bottles that are standing
  // in the wrong cabinet to answer it tonight.
  const groups = useMemo(() => groupByUrgency(candidates, CURRENT_YEAR), [candidates]);

  /**
   * What a wine's row opens on: nothing chosen, and the shelf the cellar's own
   * temperatures point at.
   *
   * Nought rather than *all of them*, for the reason the stocktake preselects
   * no count either: a row that arrived already saying *move all three* would
   * let a tap write three moves that nobody made. The shelf is the opposite
   * case and is filled in, because it is a proposal about where a bottle
   * belongs rather than a claim that anything was carried.
   */
  const chosenFor = (wine: ServingCandidate): Chosen =>
    chosen.get(wine.id) ?? {
      count: 0,
      shelfId: chooseServingShelf(wine.type, shelves) ?? "",
    };

  const choose = (wineId: string, next: Chosen) =>
    setChosen((current) => new Map(current).set(wineId, next));

  /** The bottles to send, gathered by the shelf they are going to. */
  const byShelf = useMemo(() => {
    const gathered = new Map<string, string[]>();
    for (const wine of candidates) {
      const pick = chosen.get(wine.id);
      if (pick === undefined || pick.count === 0 || pick.shelfId === "") continue;
      // Taken off the front of the list rather than chosen, exactly as the
      // stocktake resolves *two of the three*: bottles of one wine on one
      // shelf are indistinguishable to a person and are objects to the
      // history, and it is this screen's job to turn the one into the other.
      const going = wine.bottles.slice(0, pick.count).map((bottle) => bottle.id);
      gathered.set(pick.shelfId, [...(gathered.get(pick.shelfId) ?? []), ...going]);
    }
    return gathered;
  }, [candidates, chosen]);

  /**
   * Whether the cabinet gives the rule anything to choose on.
   *
   * Two shelves and no temperature on either is the state every cellar starts
   * in, and it is the one where the page has to explain itself: the proposal
   * is missing because nobody has said how cold the shelves are, not because
   * the page failed to make one.
   */
  const nothingToGoOn =
    shelves.length > 1 && shelves.every((shelf) => shelf.temperatureC === null);

  const carrying = [...byShelf.values()].reduce(
    (total, bottles) => total + bottles.length,
    0,
  );

  /**
   * Carry them over.
   *
   * One request per destination shelf, because that is what the move route
   * takes — a move is *these bottles onto that shelf*, and two shelves are two
   * moves however they were chosen. They go in sequence rather than at once so
   * that a failure stops at the first one instead of leaving two half-answers
   * to reconcile.
   */
  const carry = async () => {
    setMove({ status: "moving" });
    let bottles = 0;
    const overfull: string[] = [];
    try {
      for (const [shelfId, bottleIds] of byShelf) {
        const result = await moveBottles(cellarId, { toShelfId: shelfId, bottleIds });
        bottles += result.moved;
        // Capacity warns and does not refuse, per project-scope.md. The
        // cabinet is the authority on what fits in it; this is the record
        // saying it expected fewer.
        if (result.shelfCapacity !== null && result.shelfCount > result.shelfCapacity) {
          const shelf = shelves.find((row) => row.shelfId === shelfId);
          overfull.push(
            t("serving.overfull", {
              shelf:
                shelf === undefined ? "" : `${shelf.storageName} · ${shelf.shelfName}`,
              count: result.shelfCount,
              capacity: result.shelfCapacity,
            }),
          );
        }
      }
      setMove({ status: "moved", bottles, overfull });
      setChosen(new Map());
      load();
    } catch {
      setMove({ status: "failed" });
    }
  };

  // This page is an instruction to carry bottles from one cabinet to another,
  // and it ends in a move. A guest is not being asked to fetch anything.
  if (mayWrite === false) {
    return (
      <section>
        <CellarHeading cellarId={cellarId} current="serving" />
        <OwnersOnly cellarId={cellarId} />
      </section>
    );
  }

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="serving" />
      <p className="text-muted mt-2 max-w-prose text-sm">{t("serving.intro")}</p>

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("wines.loadFailed")}
        </p>
      )}

      {/* Why nothing is proposed, where nothing can be. The rule refuses to
          guess between two shelves it knows nothing about — which is right,
          and silent: a page that simply opened on *Välj hylla* would look like
          it had forgotten rather than like it was declining to invent. One
          sentence, and it points at the page where the two figures are typed
          in. */}
      {nothingToGoOn && (
        <p className="text-muted mt-2 max-w-prose text-sm">
          {t("serving.noTemperature")}{" "}
          <Link
            to={`/kallare/${cellarId}/skap`}
            className="text-accent underline-offset-4 hover:underline"
          >
            {t("serving.noTemperatureHint")}
          </Link>
        </p>
      )}

      {moves !== null && (
        <>
          {move.status === "moved" && (
            <div className="border-line mt-6 border-t pt-4">
              <p className="text-sm">{t("serving.moved", { count: move.bottles })}</p>
              {move.overfull.map((line) => (
                <p key={line} className="text-muted mt-1 max-w-prose text-sm">
                  {line}
                </p>
              ))}
            </div>
          )}

          {shelves.length === 0 ? (
            <p className="text-muted mt-8 max-w-prose text-sm">
              {t("serving.noServing")}{" "}
              <Link
                to={`/kallare/${cellarId}/skap`}
                className="text-accent underline-offset-4 hover:underline"
              >
                {t("serving.noServingHint")}
              </Link>
            </p>
          ) : groups.length === 0 ? (
            <p className="text-muted mt-8 max-w-prose text-sm">{t("serving.empty")}</p>
          ) : (
            <>
              {groups.map((group) => (
                <section key={group.urgency} className="mt-10">
                  <div className="flex items-baseline justify-between gap-4">
                    <h2 className="font-display text-ink text-base">
                      {t(`toOpen.group.${group.urgency}.title`)}
                    </h2>
                    <span className="text-muted shrink-0 text-sm">
                      {t("toOpen.count", { count: group.wines.length })}
                    </span>
                  </div>
                  <p className="text-muted mt-1 max-w-prose text-sm">
                    {t(`toOpen.group.${group.urgency}.note`)}
                    {group.allOnImport && <> {t("toOpen.restsOnImportAll")}</>}
                  </p>

                  <ul className="border-line mt-4 border-t">
                    {group.wines.map(({ wine, onImport }) => (
                      <WineToCarry
                        key={wine.id}
                        cellarId={cellarId}
                        wine={wine}
                        shelves={shelves}
                        chosen={chosenFor(wine)}
                        onChoose={(next) => choose(wine.id, next)}
                        {...(onImport && !group.allOnImport
                          ? { caveat: t("toOpen.restsOnImport") }
                          : {})}
                      />
                    ))}
                  </ul>
                </section>
              ))}

              <div className="border-line mt-10 border-t pt-6">
                {move.status === "failed" && (
                  <p role="alert" className="text-past mb-3 max-w-prose text-sm">
                    {t("serving.moveFailed")}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => void carry()}
                  disabled={carrying === 0 || move.status === "moving"}
                  className={actionButton}
                >
                  {/* Named before it has a number, because *Flytta 0 flaskor*
                      on a button nobody has touched yet is the interface
                      counting out loud rather than saying what it does. */}
                  {move.status === "moving"
                    ? t("serving.moving")
                    : carrying === 0
                      ? t("serving.moveNone")
                      : t("serving.move", { count: carrying })}
                </button>
              </div>
            </>
          )}

          <p className="text-muted mt-10 max-w-prose text-sm">
            {t("serving.looked", { count: moves.ageing })}{" "}
            {moves.waiting.bottles > 0 && (
              <>{t("serving.waiting", { count: moves.waiting.bottles })} </>
            )}
            {t("serving.atServing", { count: moves.atServing })}
          </p>
        </>
      )}
    </section>
  );
}

/**
 * One wine to carry: how many, and onto which shelf.
 *
 * The row is laid out like every other row in this application — the number in
 * the left gutter, because it is on the label of the bottle being reached for
 * — and then it asks the two questions a move consists of. Where the bottles
 * are standing is said in the cabinet's own words, since that is the door to
 * open.
 */
function WineToCarry({
  cellarId,
  wine,
  shelves,
  chosen,
  onChoose,
  caveat,
}: {
  cellarId: string;
  wine: ServingCandidate;
  shelves: readonly ServingShelfOption[];
  chosen: Chosen;
  onChoose: (next: Chosen) => void;
  caveat?: string;
}) {
  const { t } = useTranslation();
  const here = wine.bottles.length;
  /** The only shelf there is, where there is only one. */
  const only = shelves.length === 1 ? shelves[0] : undefined;

  /** Where they are standing, one clause per shelf, counted. */
  const places = [
    ...wine.bottles
      .reduce((counted, bottle) => {
        const where = `${bottle.storageName} · ${bottle.shelfName}`;
        return counted.set(where, (counted.get(where) ?? 0) + 1);
      }, new Map<string, number>())
      .entries(),
  ].map(([place, count]) => t("serving.standing", { count, place }));

  return (
    <li className="border-line border-b py-5">
      <div className="grid grid-cols-[2.75rem_1fr] gap-x-4">
        <div className="font-display text-muted pt-0.5 text-right text-lg">
          {wine.number}
        </div>
        <div className="min-w-0">
          <div className="flex items-baseline justify-between gap-4">
            <h3 className="font-display truncate text-lg leading-tight">
              <Link
                to={`/kallare/${cellarId}/viner/${wine.id}`}
                className="hover:text-accent underline-offset-4 hover:underline"
              >
                {wine.name}
              </Link>
            </h3>
            <span className="font-display text-muted shrink-0 text-base">
              {wine.isNonVintage ? "NV" : (wine.vintage ?? "—")}
            </span>
          </div>
          <p className="text-muted mt-0.5 truncate text-sm">{wine.producer}</p>
          <p className="text-muted mt-1 text-sm">{places.join(", ")}</p>
          {/* The band, for the reason it is on the *Att öppna* row: it is the
              answer to *why this bottle*, and this page is asking somebody to
              walk to a cabinet on the strength of it. Without it the row is a
              form, and the one loud element in this interface is missing from
              the page whose whole subject it is. */}
          <DrinkingWindow years={wine} currentYear={CURRENT_YEAR} />
          {caveat !== undefined && (
            <p className="text-muted mt-1 max-w-prose text-sm">{caveat}</p>
          )}

          <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-2">
            <span className="text-muted text-sm">{t("serving.howMany")}</span>
            {/* One button per bottle, and none for nought. Not carrying is
                what an untouched row already says, and a nought standing
                lit at rest — which is what the first draft drew — reads as
                an answer somebody gave. Tapping the lit number again puts
                the row back, the way the stocktake's counts do. */}
            {Array.from({ length: here }, (_, index) => index + 1).map((count) => (
              <button
                key={count}
                type="button"
                aria-pressed={chosen.count === count}
                aria-label={t("serving.carry", { count })}
                onClick={() =>
                  onChoose({ ...chosen, count: chosen.count === count ? 0 : count })
                }
                className={`font-display hover:bg-raised h-9 min-w-9 rounded-sm border text-sm transition-colors ${
                  chosen.count === count ? narrowed : resting
                }`}
              >
                {count}
              </button>
            ))}
          </div>

          {/* Where they are going, said before anything is chosen.
              It was behind the count until 2026-09-15, when the first look at
              this page in production found the flaw: a page whose whole claim
              is that it **proposes a shelf** showed the proposal only to
              somebody who had already tapped a number. Half the instruction —
              which door, which shelf — was invisible at exactly the moment it
              is read, standing in front of the other cabinet.

              A select rather than a sentence, and always the same height, so
              the row does not change shape under the thumb that is about to
              press a number. Nothing moves without a count, so a filled-in
              shelf on an untouched row promises nothing: it says *if you carry
              any of these, this is where they go*. */}
          {only === undefined ? (
            <div className="mt-3 max-w-xs">
              <label className={fieldLabel} htmlFor={`${wine.id}-shelf`}>
                {t("serving.destination")}
              </label>
              <div className="mt-1">
                <Select
                  id={`${wine.id}-shelf`}
                  value={chosen.shelfId}
                  onChange={(shelfId) => onChoose({ ...chosen, shelfId })}
                >
                  <option value="">{t("serving.pickShelf")}</option>
                  {shelves.map((shelf) => (
                    <option key={shelf.shelfId} value={shelf.shelfId}>
                      {shelf.storageName} · {shelf.shelfName}
                      {shelf.temperatureC === null ? "" : ` (${shelf.temperatureC}°)`}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          ) : (
            // One shelf is one answer, and a select holding a single option is
            // a question nobody was asking — but the destination is still
            // named, because *where does it go* is half of what the row is for.
            <p className="text-muted mt-3 text-sm">
              {t("serving.goesTo", {
                place: `${only.storageName} · ${only.shelfName}`,
              })}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}
